/**
 * Shopify style label overrides.
 * Key: `${doctype}:${fieldname}`
 * Value: Per-language override labels
 * 
 * Note: Some overrides like "SKU" may be the same in all languages,
 * while others like "Product Name" should be localized.
 */
export const shopifyOverrides: Record<string, { en: string; th: string }> = {
  // Item overrides
  "Item:item_code": { en: "SKU", th: "SKU" }, // SKU is universal
  "Item:item_name": { en: "Product Name", th: "ชื่อสินค้า" },
  "Item:item_group": { en: "Category", th: "หมวดหมู่" },
  "Item:stock_uom": { en: "Unit", th: "หน่วย" },
  "Item:description": { en: "Description", th: "รายละเอียด" },
  "Item:item_type": { en: "Type", th: "ประเภท" },
  "Item:has_variants": { en: "Has Variants", th: "มีตัวแปร" },
  "Item:brand": { en: "Brand", th: "แบรนด์" },
  "Item:manufacturer": { en: "Manufacturer", th: "ผู้ผลิต" },
  "Item:manufacturer_part_no": { en: "Part Number", th: "หมายเลขชิ้นส่วน" },
  "Item:standard_rate": { en: "Standard Price", th: "ราคามาตรฐาน" },
  "Item:valuation_rate": { en: "Valuation Rate", th: "อัตราตีราคา" },
  "Item:last_purchase_rate": { en: "Last Purchase Price", th: "ราคาซื้อล่าสุด" },
  "Item:base_unit_rate": { en: "Base Unit Rate", th: "อัตราหน่วยฐาน" },
  "Item:is_purchase_item": { en: "Purchase Item", th: "สินค้าซื้อ" },
  "Item:is_stock_item": { en: "Stock Item", th: "สินค้าคงคลัง" },
  "Item:include_item_in_manufacturing": { en: "Include in Manufacturing", th: "รวมในการผลิต" },
  "Item:is_fixed_asset": { en: "Fixed Asset", th: "สินทรัพย์ถาวร" },
  "Item:default_warehouse": { en: "Default Warehouse", th: "คลังสินค้าเริ่มต้น" },
  "Item:safety_stock": { en: "Safety Stock", th: "สต็อกความปลอดภัย" },
  "Item:disabled": { en: "Disabled", th: "ปิดใช้งาน" },
  "Item:end_of_life": { en: "End of Life", th: "สิ้นสุดอายุการใช้งาน" },
  
  // Sales Order overrides (Shopify-friendly labels)
  // Header/Order Overview section
  "Sales Order:name": { en: "Order", th: "คำสั่งซื้อ" },
  "Sales Order:status": { en: "Status", th: "สถานะ" },
  "Sales Order:transaction_date": { en: "Order Date", th: "วันที่สั่งซื้อ" },
  "Sales Order:delivery_date": { en: "Delivery Date", th: "วันที่จัดส่ง" },
  "Sales Order:order_type": { en: "Type", th: "ประเภท" },
  "Sales Order:company": { en: "Company", th: "บริษัท" },
  "Sales Order:currency": { en: "Currency", th: "สกุลเงิน" },
  
  // Customer
  "Sales Order:customer": { en: "Customer", th: "ลูกค้า" },
  
  // Totals
  "Sales Order:grand_total": { en: "Total", th: "รวม" },
  "Sales Order:net_total": { en: "Subtotal", th: "ยอดรวมย่อย" },
  "Sales Order:total_taxes_and_charges": { en: "Tax", th: "ภาษี" },
  "Sales Order:total_qty": { en: "Total Quantity", th: "จำนวนรวม" },
  
  // Discounts
  "Sales Order:discount_amount": { en: "Discount", th: "ส่วนลด" },
  "Sales Order:additional_discount_amount": { en: "Additional Discount", th: "ส่วนลดเพิ่มเติม" },
  
  // Status Summary
  "Sales Order:delivery_status": { en: "Delivery Status", th: "สถานะการจัดส่ง" },
  "Sales Order:per_delivered": { en: "% Delivered", th: "% จัดส่งแล้ว" },
  "Sales Order:per_billed": { en: "% Billed", th: "% เรียกเก็บแล้ว" },
  "Sales Order:per_picked": { en: "% Picked", th: "% เตรียมแล้ว" },
  
  // Sales Order Item overrides (child table)
  "Sales Order Item:item_code": { en: "SKU", th: "SKU" },
  "Sales Order Item:item_name": { en: "Product", th: "สินค้า" },
  "Sales Order Item:qty": { en: "Quantity", th: "จำนวน" },
  "Sales Order Item:rate": { en: "Price", th: "ราคา" },
  "Sales Order Item:amount": { en: "Total", th: "รวม" },
  "Sales Order Item:discount_amount": { en: "Discount", th: "ส่วนลด" },
  
  // Item Price overrides (Shopify-friendly labels)
  "Item Price:item_code": { en: "SKU", th: "SKU" },
  "Item Price:item_name": { en: "Product Name", th: "ชื่อสินค้า" },
  "Item Price:price_list": { en: "Price List", th: "รายการราคา" },
  "Item Price:currency": { en: "Currency", th: "สกุลเงิน" },
  "Item Price:price_list_rate": { en: "Price", th: "ราคา" },
  "Item Price:uom": { en: "Unit", th: "หน่วย" },
  "Item Price:packing_unit": { en: "Packing Unit", th: "หน่วยบรรจุ" },
  "Item Price:buying": { en: "Buying", th: "ซื้อ" },
  "Item Price:selling": { en: "Selling", th: "ขาย" },
  "Item Price:valid_from": { en: "Valid From", th: "ใช้ได้ตั้งแต่" },
  "Item Price:valid_upto": { en: "Valid Until", th: "ใช้ได้จนถึง" },
  "Item Price:customer": { en: "Customer", th: "ลูกค้า" },
  "Item Price:supplier": { en: "Supplier", th: "ผู้ขาย" },
  "Item Price:batch_no": { en: "Batch No", th: "หมายเลขล็อต" },
  // Add more as needed
};

